import { useCallback, useEffect, useState } from "react";
import { useAccount, usePublicClient, useWriteContract, useSwitchChain } from "wagmi";
import { Trophy, Crown, Timer } from "lucide-react";
import { toast } from "sonner";
import pot from "../../lib/potArtifact.json";
import { api, errMsg } from "../../lib/api";
import { PHASE, fmtAmt, fmtLeft } from "../../lib/competition";
import { addrUrl } from "../../lib/chains";
import { Panel } from "../State";
import { TxStatus, runTx } from "../TxStatus";

const useNow = () => {
  const [n, setN] = useState(Date.now() / 1000);
  useEffect(() => { const t = setInterval(() => setN(Date.now() / 1000), 1000); return () => clearInterval(t); }, []);
  return n;
};

const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;

const Board = ({ c }) => (
  <ol className="divide-y divide-white/5" data-testid={`comp-leaderboard-${c.id}`}>
    {!c.leaderboard.length && <li className="py-6 text-center text-xs text-slate-500" data-testid="comp-leaderboard-empty">No trades counted yet — swap on this page to enter.</li>}
    {c.leaderboard.map((r) => (
      <li key={r.trader} className="flex items-center gap-3 py-2 text-xs" data-testid={`comp-rank-${r.rank}`}>
        <span className={`w-6 font-mono font-black ${r.rank <= c.winners ? "text-lime" : "text-slate-500"}`}>{r.rank <= c.winners && r.rank === 1 ? <Crown className="h-4 w-4" /> : `#${r.rank}`}</span>
        <a href={addrUrl(r.trader)} target="_blank" rel="noreferrer" className="font-mono text-slate-200 hover:text-lime">{r.alias || short(r.trader)}</a>
        <span className="ml-auto font-mono text-white">${r.score_usd.toLocaleString()}</span>
        <span className="w-16 text-right text-slate-500">{r.trades} trade{r.trades === 1 ? "" : "s"}</span>
        {r.rank <= c.winners && <span className="w-14 text-right text-lime">{c.split_pcts[r.rank - 1]}%</span>}
      </li>
    ))}
  </ol>
);

const Release = ({ c, onDone }) => {
  const { isConnected, chainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const pc = usePublicClient({ chainId: 4663 });
  const { writeContractAsync } = useWriteContract();
  const [tx, setTx] = useState(null);
  const go = async () => {
    try {
      const p = c.payout || (await api.post(`/competitions/${c.id}/finalize`)).data.payout;
      if (chainId !== 4663) await switchChainAsync({ chainId: 4663 });
      const rc = await runTx({ pc, setTx, chainId: 4663, label: "Release rewards", send: () => writeContractAsync({ chainId: 4663, address: c.pot, abi: pot.abi, functionName: "release", args: [p.winners, p.amounts.map(BigInt), p.signature] }) });
      if (rc) { await api.post(`/competitions/${c.id}/released`, { tx_hash: rc.transactionHash }); toast.success("Rewards sent to the winners"); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <button data-testid="comp-release-button" disabled={!isConnected} onClick={go} className="btn-lime w-full py-2.5 text-xs disabled:opacity-40">{isConnected ? "Release rewards to winners" : "Connect a wallet to release rewards"}</button>
      <p className="text-[10px] text-slate-500">Anyone can submit the payout. The pot contract only accepts the HIVEVORA-signed results.</p>
      <TxStatus tx={tx} testid="comp-release-tx" />
    </div>
  );
};

const Stat = ({ k, v, testid }) => <div><p className="text-[10px] uppercase tracking-widest text-slate-500">{k}</p><p className="font-mono text-sm text-white" data-testid={testid}>{v}</p></div>;

const CompCard = ({ c, reload }) => {
  const now = useNow();
  const [label, tone] = PHASE[c.phase] || PHASE.ended;
  const sym = c.token_info?.symbol;
  const left = c.phase === "upcoming" ? c.start_ts - now : c.end_ts - now;
  return (
    <div className="p-5 space-y-4" data-testid={`comp-card-${c.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-lg font-black text-white">{c.title}</p>
        <span className={`rounded-full border border-current px-2 py-0.5 text-[10px] font-bold uppercase ${tone}`} data-testid="comp-phase">{label}</span>
        {(c.phase === "live" || c.phase === "upcoming") && <span className="ml-auto flex items-center gap-1 font-mono text-sm text-lime" data-testid="comp-countdown"><Timer className="h-4 w-4" /> {c.phase === "upcoming" ? "starts in" : "ends in"} {fmtLeft(left)}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat k="Reward pot" v={`${fmtAmt(c.funded_amount || c.reward_amount, c.token_info?.decimals)} ${sym}`} testid="comp-pot" />
        <Stat k="Winners" v={`Top ${c.winners}`} testid="comp-winners" />
        <Stat k="Ranked by" v={c.metric_label} testid="comp-metric" />
        <Stat k="Window" v={`${new Date(c.start_ts * 1000).toLocaleString()} → ${new Date(c.end_ts * 1000).toLocaleString()}`} testid="comp-window" />
      </div>
      <p className="text-[11px] text-slate-400">Only swaps made with the swap box on this page count{c.min_volume_usd ? `, minimum $${c.min_volume_usd} to qualify` : ""}. Split: {c.split_pcts.map((p, i) => `#${i + 1} ${p}%`).join(" · ")}. {c.leftover === "refund" ? "Unfilled places return to the project." : "Unfilled places are shared among the winners."} Rewards are locked in the <a className="text-lime hover:underline" href={addrUrl(c.pot)} target="_blank" rel="noreferrer">pot contract</a>.</p>
      <Board c={c} />
      {c.phase === "ended" && <Release c={c} onDone={reload} />}
      {c.phase === "paid" && c.payout && <p className="text-xs text-lime" data-testid="comp-paid-note">Paid {c.payout.winners.length} winner(s) on-chain.</p>}
    </div>
  );
};

export const CompetitionBoard = ({ address }) => {
  const [comps, setComps] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/competitions/token/${address}`);
      const shown = data.competitions.filter((c) => ["upcoming", "live", "ended", "paid"].includes(c.phase)).slice(0, 2);
      setComps(await Promise.all(shown.map((c) => api.get(`/competitions/${c.id}`).then((r) => r.data))));
    } catch { setComps([]); }
  }, [address]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const has = comps.length > 0;
  useEffect(() => { if (has && window.location.hash === "#competition") document.getElementById("competition")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [has]);
  if (!comps.length) return null;
  return (
    <Panel title="Trading competition" icon={Trophy} testid="token-competition-panel">
      <div id="competition" className="scroll-mt-24 divide-y divide-white/5">{comps.map((c) => <CompCard key={c.id} c={c} reload={load} />)}</div>
    </Panel>
  );
};
